import {SenkronPlanDosya} from "@/services/senkronPlanService";
import {IAvukatDosya} from "@/plugins/uyap-plugin/uyap/CommonInterfaces";
import {
    secimdenCikar,
    secimeEkle
} from "@/components/forms/form-wizard/eicrapro/genelIslemTakipFiltre";

/**
 * Plan dosya seçiminin saf mantığı (Faz 3, DEV-2966).
 *
 * ELLE GİRİŞ KALDIRILDI (manuel test geri bildirimi): kullanıcılar birim
 * kimliğini bilmiyor. Dosyalar artık ya UYAP listelemesinden ya da Excel'den
 * geliyor; Excel'de birim adı `birim_id`ye {@link excelDosyaCozumleme} ile
 * çözülüyor.
 *
 * Dosyalar `dosyaId` ile SAKLANMAZ: UYAP `dosyaId`si volatil, oturumdan oturuma
 * değişiyor. Plan `birim_id + dosya_esas_no` ikilisini tutar — bu, snapshot'ın
 * `dosya_ref`i ("<birimId>:<dosyaEsasNo>") ile aynı ikilidir; koşum anında
 * `DosyaBul` dosyayı taze çözer.
 */

/** Tekillik anahtarı; snapshot `dosya_ref` biçimiyle bilerek aynı. */
export function dosyaAnahtari(dosya: { birim_id: any; dosya_esas_no: any }): string {
    return `${String(dosya.birim_id ?? "").trim()}:${String(dosya.dosya_esas_no ?? "").trim()}`;
}

/**
 * UYAP listeleme (ya da Excel) satırlarını plan dosyalarına çevirir.
 *
 * `birim_adi` olarak ÇÖZÜLEN UYAP adı (`eslesenBirimAdi`) tercih edilir, varsa.
 * Excel'de kullanıcı "Ankara 7. İcra Dairesi" yazıp UYAP'ta karşılığı
 * "Ankara 7. Genel İcra Dairesi" olabiliyor; planda kullanıcının yazdığı ham metni
 * saklamak, dosyanın gerçekte hangi daire olduğunu gizler. Eşleştirme zaten
 * `birim_id` ile yapılıyor; `birim_adi` yalnız okunabilirlik için ve doğru olanı
 * UYAP'ın kendi yazımı. UYAP listelemesinden gelen satırlarda bu alan hiç
 * bulunmuyor, davranış değişmiyor.
 */
export function avukatDosyalariniPlanaCevir(
    dosyalar: Array<IAvukatDosya>
): Array<SenkronPlanDosya> {
    return (dosyalar ?? [])
        .filter((dosya) => dosya && dosya.birimId && dosya.dosyaNo)
        .map((dosya) => {
            const cozulen = ((dosya as any).eslesenBirimAdi ?? "").trim();
            const ham = (dosya.birimAdi ?? "").trim();
            return {
                birim_id: String(dosya.birimId).trim(),
                dosya_esas_no: String(dosya.dosyaNo).trim(),
                birim_adi: (cozulen || ham) || undefined
            };
        });
}

/**
 * Verilen anahtar fonksiyonuna göre listeyi tekilleştirir; İLK görülen kalır,
 * SIRA KORUNUR. `dosyalariBirlestir`in kullandığı `gorulen` desenidir —
 * dosya listesi tekilleştirmesi gereken her yerin (birleştirme, Adım 3 özet
 * sayımı) aynı mantığı paylaşması için buraya çıkarıldı (DEV-2603): Excel'de
 * ya da tabloda mükerrer satır varsa tekilleştirilmeden sayılan bir "eklenecek
 * sayısı" 500 dosya tavanını yanlışlıkla tetikleyebiliyordu (B25 regresyonu).
 */
export function anahtarBazliTekillestir<T>(
    ogeler: Array<T>,
    anahtarGetir: (oge: T) => string
): Array<T> {
    const sonuc: Array<T> = [];
    const gorulen: Record<string, boolean> = {};

    (ogeler ?? []).forEach((oge) => {
        const anahtar = anahtarGetir(oge);
        if (gorulen[anahtar]) return;
        gorulen[anahtar] = true;
        sonuc.push(oge);
    });

    return sonuc;
}

/**
 * İki listeyi birleştirir; MEVCUT SIRA KORUNUR, yeni olanlar sona eklenir.
 *
 * Sıra korunuyor çünkü sunucu `dosya_secimi` JSON'unda liste sırasını anlamlı
 * sayıyor: yeniden sıralanmış ama içeriği aynı bir liste "değişti" görünür ve
 * gecenin paketini boşuna iptal ettirir (aynı gerekçe `alanlar` için de
 * geçerli, bkz. `senkronAlanFrekans.alanSatirlariniPlanaCevir`).
 *
 * Tekrar eden dosya sunucuda 422 — birleştirme burada tekilleştirir.
 */
export function dosyalariBirlestir(
    mevcut: Array<SenkronPlanDosya>,
    yeni: Array<SenkronPlanDosya>
): Array<SenkronPlanDosya> {
    const adaylar = [...(mevcut ?? []), ...(yeni ?? [])].filter(
        (dosya) => dosyaAnahtari(dosya) !== ":"
    );
    return anahtarBazliTekillestir(adaylar, dosyaAnahtari);
}

/** Tek dosyayı listeden düşürür. */
export function dosyaCikar(
    dosyalar: Array<SenkronPlanDosya>,
    anahtar: string
): Array<SenkronPlanDosya> {
    return (dosyalar ?? []).filter((dosya) => dosyaAnahtari(dosya) !== anahtar);
}

/**
 * Sihirbaz Adım 3'te toplam dosya sayısı azami sınırı aşıyor mu (DEV-2603
 * B25). Sunucudaki gerçek sınır `SenkronPlanKaydetRequest`de (max:500, bkz.
 * `senkronPlanGovde.AZAMI_DOSYA_UYARI_ESIGI`); bu yalnız aynı karşılaştırmayı
 * bileşenden ayırıp test edilebilir kılıyor — `.vue` dosyaları mount
 * edilmeden test edilemiyor.
 */
export function dosyaSayisiAsildiMi(toplamSonrasi: number, azami: number): boolean {
    return toplamSonrasi > azami;
}

/** Görünür satırların tamamı seçili mi (başlıktaki kutunun hâli). */
export function tumuSecildiMi(
    secilenler: Array<any>,
    gorunurler: Array<any>
): boolean {
    if (!gorunurler || gorunurler.length === 0) return false;
    const seciliAnahtarlar = new Set((secilenler ?? []).map((satir) => satir?.index));
    return gorunurler.every((satir) => seciliAnahtarlar.has(satir?.index));
}

/**
 * "Tümünü seç" başlık kutusunun sonucu.
 *
 * FİLTREYE GİREN TÜM SATIRLAR üzerinde çalışır, yalnız görünen sayfada değil:
 * Vuetify'nin varsayılan "tümünü seç"i tabloda o an render edilen sayfayı
 * seçiyor, dolayısıyla 15 satırlık bir filtrede kullanıcı 10 satır seçilmiş
 * buluyordu. Genel İşlem sihirbazı da bu yüzden başlık slotunu eziyor.
 *
 * Filtrenin GİZLEDİĞİ seçimlere dokunulmaz — seçim filtreler arası birikimlidir
 * (DEV-2720'de bilinçli olarak korunan davranış).
 */
export function tumunuSecUygula(
    secilenler: Array<any>,
    gorunurler: Array<any>
): Array<any> {
    return tumuSecildiMi(secilenler, gorunurler)
        ? secimdenCikar(secilenler, gorunurler)
        : secimeEkle(secilenler, gorunurler);
}
